/**
 * Обращение к API Ассистента.
 *
 * Фронтенд не принимает решений о доступе (§16.2): при истёкшей сессии
 * бэкенд отвечает 401 и адресом портала, и мы просто переходим туда.
 */

import type {
  WhatIfField,
  WhatIfResult,
  OutcomeBankRow,
  OutcomeRecord,
  FeedbackList,
  RegistryReport,
  Draft,
  DraftSummary,
  MatchReport,
  MatchResultResponse,
  NextQuestion,
  QuarantineList,
  Questionnaire,
  Answers,
  RosterEntry,
  SaveResult,
  SessionInfo,
  SyncChanges,
  SyncRunResult,
  SyncStatus,
} from './types'

export class SessionExpired extends Error {
  constructor(readonly portalUrl: string) {
    super('Сессия истекла')
  }
}

/** Ошибка сервера с исходными подробностями (например, перечнем вопросов). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail: unknown,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    credentials: 'same-origin',
  })

  if (response.status === 401) {
    const body = await response.json().catch(() => ({}))
    throw new SessionExpired(body.portal_url ?? '/')
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new ApiError(errorText(body.detail, response.status), response.status, body.detail)
  }
  return response.json() as Promise<T>
}

/** Понятный текст ошибки сервера: строка как есть; «не хватает ответов» —
 *  с перечнем вопросов; иначе — номер ошибки (21.10.2026: брокер видел
 *  только «Ошибка 422» и не понимал, что делать). */
function errorText(detail: unknown, status: number): string {
  if (typeof detail === 'string') return detail
  if (detail && typeof detail === 'object') {
    const d = detail as { ошибка?: string; вопросы?: string[] }
    if (d.ошибка) {
      const questions = d.вопросы?.length ? `: ${d.вопросы.join('; ')}` : ''
      return `${d.ошибка[0].toUpperCase()}${d.ошибка.slice(1)}${questions}. Откройте анкету и ответьте на эти вопросы.`
    }
  }
  return `Ошибка ${status}`
}

export const api = {
  questionnaire: () => request<Questionnaire>('/api/intake/questionnaire'),

  listDrafts: () =>
    request<{ анкеты: DraftSummary[] }>('/api/intake/drafts').then((r) => r.анкеты),

  createDraft: (label?: string) =>
    request<{ id: string; revision: number }>('/api/intake/drafts', {
      method: 'POST',
      body: JSON.stringify({ client_label: label ?? null }),
    }),

  getDraft: (id: string) => request<Draft>(`/api/intake/drafts/${id}`),

  setDraftLabel: (id: string, label: string | null) =>
    request<{ id: string; метка: string | null }>(`/api/intake/drafts/${id}/label`, {
      method: 'PATCH',
      body: JSON.stringify({ client_label: label }),
    }),

  deleteDraft: (id: string) =>
    request<{ ok: boolean }>(`/api/intake/drafts/${id}`, { method: 'DELETE' }),

  nextQuestion: (id: string) => request<NextQuestion>(`/api/intake/drafts/${id}/next`),

  saveAnswers: (id: string, answers: Answers, baseRevision: number, device: string) =>
    request<SaveResult>(`/api/intake/drafts/${id}/answers`, {
      method: 'PATCH',
      body: JSON.stringify({ answers, base_revision: baseRevision, device }),
    }),

  me: () => request<{ full_name: string | null; role: string; branch: string | null }>('/api/me'),

  /** Список для вопроса с `options_from`: приходит из снимка таблицы (§14.1). */
  reference: (name: string, city?: string) =>
    request<{ значения: string[]; предупреждение?: string | null }>(
      `/api/intake/reference/${name}${city != null ? `?city=${encodeURIComponent(city)}` : ''}`,
    ),

  /** Выполнить подбор по анкете (§6, §10, экран /result). */
  result: (draftId: string) =>
    request<MatchResultResponse>(`/api/result/${draftId}`, { method: 'POST' }),

  /** Клиентский PDF по белому списку полей (§16.4) — как файл, не JSON. */
  resultPdf: async (draftId: string): Promise<Blob> => {
    const response = await fetch(`/api/result/${draftId}/pdf`, {
      method: 'POST',
      credentials: 'same-origin',
    })
    if (response.status === 401) {
      const body = await response.json().catch(() => ({}))
      throw new SessionExpired(body.portal_url ?? '/')
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new ApiError(errorText(body.detail, response.status), response.status, body.detail)
    }
    return response.blob()
  },

  /** Активные сессии сотрудника на всех устройствах (§16.6). */
  sessions: () => request<{ sessions: SessionInfo[] }>('/api/sessions').then((r) => r.sessions),

  revokeAllSessions: () =>
    request<{ revoked: number }>('/api/sessions/revoke-all', { method: 'POST' }),

  logout: () => request<{ portal_url: string }>('/api/logout', { method: 'POST' }),

  /** Вход по ФИО и рабочему телефону (§16.3) — своя страница /login. */
  login: (fullName: string, phone: string) =>
    request<{ ok: boolean }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ full_name: fullName, phone }),
    }),

  /** Интерстишл «это ваше устройство?» после входа (§16.6) — cookie
   * становится сессионной, сессия завершится при закрытии вкладки. */
  markSharedDevice: () =>
    request<{ is_shared_device: boolean }>('/api/session/mark-shared', { method: 'POST' }),

  // ─── Админ-раздел (§16.5) — руководитель и администратор ────────────
  /** Симулятор «что если» (ТЗ 3.0 §18): поля и пересчёт без изменения анкеты. */
  whatIfFields: (draftId: string) =>
    request<{ поля: WhatIfField[]; 'роли созаёмщика': string[]; 'формы подтверждения': string[] }>(
      `/api/result/${draftId}/whatif`,
    ),
  whatIf: (draftId: string, body: { изменения: Record<string, unknown>; созаёмщик?: Record<string, unknown> | null }) =>
    request<WhatIfResult>(`/api/result/${draftId}/whatif`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Сравнение сроков (ТЗ 3.0 §19). */
  whatIfTerms: (draftId: string) =>
    request<{
      сроки: {
        'срок, лет': number
        сейчас: boolean
        вариантов: number
        лучший: { банк: string; программа: string; ставка: string; сумма: string; платёж: string; переплата: string } | null
      }[]
      пояснение: string
    }>(`/api/result/${draftId}/whatif/terms`, { method: 'POST' }),
  /** ИИ-копилот (ТЗ 3.0 §29): ответ только по данным расчёта. */
  ask: (draftId: string, question: string) =>
    request<{ вопрос: string; ответ: string; источник: string; пометка: string | null }>(
      `/api/result/${draftId}/ask`,
      { method: 'POST', body: JSON.stringify({ вопрос: question }) },
    ),
  /** Результат заявки в банк (ТЗ 3.0 §28). */
  recordOutcome: (draftId: string, outcome: Record<string, unknown>) =>
    request<OutcomeRecord>(`/api/result/${draftId}/outcomes`, {
      method: 'POST',
      body: JSON.stringify(outcome),
    }),
  outcomes: (draftId: string) =>
    request<{ результаты: OutcomeRecord[] }>(`/api/result/${draftId}/outcomes`).then(
      (r) => r.результаты,
    ),
  /** Журнал действий сотрудников (ТЗ 3.0 §32) — только администратору. */
  adminAudit: (filters: { action?: string; employee?: string }) => {
    const query = new URLSearchParams(
      Object.entries(filters).filter(([, v]) => v) as [string, string][],
    ).toString()
    return request<{
      действия: Record<string, string>
      записи: {
        id: number
        когда: string
        действие: string
        сотрудник: string | null
        роль: string | null
        объект: string | null
        подробности: Record<string, unknown>
        ip: string | null
      }[]
    }>(`/api/admin/audit${query ? `?${query}` : ''}`)
  },
  adminOutcomes: () =>
    request<{
      'по банкам': OutcomeBankRow[]
      оговорка: string
      'сверка с расчётом': {
        проверено: number
        потери: { анкета: string; банк: string; программа: string | null; 'почему система не нашла': string[] }[]
        пометка?: string
      }
    }>('/api/admin/outcomes'),
  /** Статус документа чек-листа (ТЗ 3.0 §25). */
  markDocument: (draftId: string, key: string, status: string) =>
    request<{ ключ: string; статус: string; 'статус текстом': string }>(
      `/api/result/${draftId}/documents`,
      { method: 'POST', body: JSON.stringify({ ключ: key, статус: status }) },
    ),
  adminSyncStatus: () => request<SyncStatus>('/api/admin/sync/status'),
  adminSyncRun: () => request<SyncRunResult>('/api/admin/sync/run', { method: 'POST' }),
  adminSyncChanges: () => request<SyncChanges>('/api/admin/sync/changes'),
  adminQuarantine: () => request<QuarantineList>('/api/admin/quarantine'),
  adminMatchReport: () => request<MatchReport>('/api/admin/match-report'),

  // ─── Обратная связь (D-FB-01) и проверка правил (D-REG-01) ──────────
  sendFeedback: (
    draftId: string,
    feedback: { вид: string; текст: string; банк?: string | null; программа?: string | null; контекст?: unknown },
  ) =>
    request<{ id: number; статус: string }>(`/api/result/${draftId}/feedback`, {
      method: 'POST',
      body: JSON.stringify(feedback),
    }),
  adminFeedback: (status?: string) =>
    request<FeedbackList>(`/api/admin/feedback${status ? `?status=${status}` : ''}`),
  adminFeedbackResolve: (id: number, статус: string, решение: string) =>
    request<unknown>(`/api/admin/feedback/${id}`, {
      method: 'POST',
      body: JSON.stringify({ статус, решение }),
    }),
  adminRegistry: () => request<RegistryReport>('/api/admin/registry'),
  adminRegistryDecide: (decision: {
    rule_id: string
    решение: 'approve' | 'reject'
    нижняя?: string | null
    верхняя?: string | null
    комментарий?: string | null
  }) =>
    request<{ rule_id: string; 'статус правила': string; пояснение: string | null }>(
      '/api/admin/registry/decisions',
      { method: 'POST', body: JSON.stringify(decision) },
    ),

  // ─── Список сотрудников (§16.3) — администратор ─────────────────────
  adminRoster: () =>
    request<{ сотрудников: RosterEntry[] }>('/api/admin/roster').then((r) => r.сотрудников),
  adminRosterAdd: (entry: {
    full_name: string
    phone: string
    role: string
    branch?: string | null
    department?: string | null
  }) => request<{ ok: boolean }>('/api/admin/roster', { method: 'POST', body: JSON.stringify(entry) }),
  adminRosterRemove: (phone: string) =>
    request<{ ok: boolean }>(`/api/admin/roster/${encodeURIComponent(phone)}`, {
      method: 'DELETE',
    }),
}

/** Короткая метка устройства — для отметки, откуда сохранён черновик (§16.6). */
export function deviceLabel(): string {
  const width = window.innerWidth
  const kind = width < 640 ? 'телефон' : width <= 1024 ? 'планшет' : 'компьютер'
  return `${kind} ${navigator.platform || ''}`.trim().slice(0, 64)
}
